import * as t from "io-ts"
import { KeyedMutator } from "swr"

const CommissioningSiteData = t.record(
    t.string,
    t.union([t.string, t.number, t.boolean, t.null])
)

const CommissioningDeviceData = t.record(
    t.string,
    t.union([t.string, t.number, t.boolean, t.null])
)

const Device = t.record(
    t.string,
    t.union([t.string, t.number, t.boolean, t.null])
)

const InstallerCustomerDetails = t.record(
    t.string,
    t.union([t.number, t.string, t.null, t.undefined])
)

const SupplyDetails = t.record(
    t.string,
    t.union([
        t.number,
        t.string,
        t.boolean,
        t.null,
        t.record(
            t.string,
            t.union([t.string, t.number, t.record(t.string, t.string)])
        ),
    ])
)

const ExistingDevices = t.record(
    t.string,
    t.union([t.string, t.number, t.boolean, t.null])
)

const Lcts = t.array(t.string)

const Attachments = t.intersection([
    t.strict({
        fileUrl: t.string,
    }),
    t.partial({
        fileName: t.string,
    }),
])

const SummaryFacts = t.record(t.string, t.union([t.number, t.string]))

const NewPremisesGenerationCapacityKw = t.record(
    t.string,
    t.union([t.string, t.number, t.null])
)

const NewPremisesMaxDemand = t.record(
    t.string,
    t.union([t.string, t.number, t.null])
)

const ApplicationSizes = t.strict({
    totAggGen: t.string,
    maxDemand: t.string,
})

const DnoApplicationResponse = t.union([
    t.strict({
        comment: t.union([t.string, t.null]),
        demandLimit: t.union([t.string, t.null]),
        demandLimitReasons: t.union([t.array(t.string), t.null]),
        generationLimit: t.union([t.string, t.null]),
        generationLimitReasons: t.union([t.array(t.string), t.null]),
        dnoWorksRequiredReasons: t.union([
            t.array(t.string),
            t.null,
            t.undefined,
        ]),
    }),
    t.undefined,
])

const ApplicationComment = t.type({
    comment: t.string,
    commentUsername: t.string,
    firstname: t.union([t.string, t.null]),
    lastname: t.union([t.string, t.null]),
    initials: t.union([t.string, t.null]),
    commentId: t.string,
    publishedAt: t.string,
    fromRequestor: t.boolean,
})

const ApplicationCommissioningInformation = t.strict({
    siteData: CommissioningSiteData,
    files: t.array(Attachments),
    devices: t.union([
        t.array(
            t.intersection([
                CommissioningDeviceData,
                t.strict({
                    deviceSysRef: t.string,
                }),
            ])
        ),
        t.undefined,
    ]),
})

const DisplayActionHistory = t.strict({
    title: t.string,
    time: t.string,
    isLatest: t.boolean,
    displayToDno: t.boolean,
    displayToInstaller: t.boolean,
    details: t.record(
        t.string,
        t.union([t.string, t.number, t.boolean, t.null, t.undefined])
    ),
})

const DnoCommissioningResponse = t.strict({
    exportMpan: t.union([t.string, t.null]),
    action: t.string,
    comment: t.union([t.string, t.null]),
    actionedBy: t.string,
    responseTime: t.number,
})

const Application = t.strict({
    applicationStatus: t.string,
    applicationClass: t.string,
    actionHistoryToDisplay: t.array(DisplayActionHistory),
    devicesToInstall: t.array(Device),
    installerCustomerDetails: InstallerCustomerDetails,
    applicationId: t.string,
    supplyDetails: SupplyDetails,
    lcts: Lcts,
    dno: t.string,
    dnoApplicationResponse: DnoApplicationResponse,
    dnoCommissioningResponse: t.union([
        t.array(DnoCommissioningResponse),
        t.undefined,
    ]),
    existingDevices: t.array(ExistingDevices),
    cutOutImages: t.array(Attachments),
    additionalAttachments: t.array(Attachments),
    createdDate: t.string,
    installerLastUpdated: t.union([t.number, t.undefined, t.null]),
    newPremisesGenerationCapacityKw: NewPremisesGenerationCapacityKw,
    newPremisesMaxDemand: NewPremisesMaxDemand,
    isRedacted: t.union([t.boolean, t.undefined]),
    comments: t.union([t.array(ApplicationComment), t.undefined]),
    isReadyForCx: t.boolean,
    isResubmit: t.union([t.boolean, t.undefined]),
    amendmentFailureCategory: t.union([t.string, t.undefined, t.null]),
    lastAmendmentFailed: t.union([t.boolean, t.undefined]),
    commissioningInfo: t.union([
        ApplicationCommissioningInformation,
        t.undefined,
    ]),
    isInstallerThreadUnread: t.union([t.boolean, t.undefined]),
    lastReadByInstallerUsername: t.union([t.string, t.undefined]),
    lastReadByInstallerTimestamp: t.union([t.string, t.undefined]),
})

const NextPageKey = t.union([
    t.record(t.string, t.union([t.string, t.number])),
    t.null,
])

const LastEvaluatedKey = t.union([
    t.record(t.string, t.union([t.string, t.number])),
    t.null,
])

export const InstallerSingleApplication = t.strict({
    items: t.array(Application),
})

export type TApplicationCommissioningInformation = t.TypeOf<
    typeof ApplicationCommissioningInformation
>

export type TCommissioningDeviceData = t.TypeOf<typeof CommissioningDeviceData>

export type IInstallerSingleApplications = t.TypeOf<
    typeof InstallerSingleApplication
>

export type IApplicationComment = t.TypeOf<typeof ApplicationComment>

export type IApplication = t.TypeOf<typeof Application> & {
    summaryFacts: ISummaryFacts
}

export const ListApplications = t.array(Application)

export type IListApplications = t.TypeOf<typeof ListApplications>

export const ListApplication = Application

export type IListApplication = t.TypeOf<typeof ListApplication>

export type ISummaryFacts = t.TypeOf<typeof SummaryFacts>

export type IApplicationSize = t.TypeOf<typeof ApplicationSizes>

export interface IGetInstallerSingleApplication {
    [key: string]: IInstallerSingleApplications
}

export const RawSearchResult = t.strict({
    Count: t.number,
    LastEvaluatedKey,
    Items: t.array(Application),
    ScannedCount: t.number,
})

export type IRawSearchResult = t.TypeOf<typeof RawSearchResult>

export const ApplicationListResponse = t.strict({
    totalResults: t.number,
    pageSizes: t.number,
    data: t.array(Application),
    nextPageKey: NextPageKey,
})

export type IApplicationListResponse = t.TypeOf<typeof ApplicationListResponse>

export type TGetInstallerApplications = {
    data: IListApplications | undefined
    isLoading: boolean
    isError: boolean
    isValidating: boolean
    page: number
    isFinalPage: boolean
    setPage: (
        size: number | ((_size: number) => number)
    ) => Promise<IApplicationListResponse[] | undefined>
}

export type TDisplayActionHistory = t.TypeOf<typeof DisplayActionHistory>

const Draft = t.strict({
    draftId: t.string,
    mainAddress: t.string,
    postCode: t.string,
    lastUpdated: t.string,
    completionStatus: t.number,
    lcts: t.array(t.string),
    installerUserName: t.string,
})
export const ListDrafts = t.array(Draft)

export type IDraftApplications = t.TypeOf<typeof ListDrafts>

export const ListDraft = Draft

export type IDraftApplication = t.TypeOf<typeof ListDraft>

export type TGetInstallerDraftApplications = {
    data: IDraftApplications | undefined
    isLoading: boolean
    isError: boolean
    isValidating: boolean
    page: number
    isFinalPage: boolean
    setPage: (
        size: number | ((_size: number) => number)
    ) => Promise<IDraftApplicationListResponse[] | undefined>
    mutate: KeyedMutator<IDraftApplicationListResponse[]>
}

export const DraftApplicationListResponse = t.strict({
    totalResults: t.number,
    pageSizes: t.number,
    data: t.array(Draft),
    nextPageKey: NextPageKey,
})

export type IDraftApplicationListResponse = t.TypeOf<
    typeof DraftApplicationListResponse
>

export const RawDraftSearchResult = t.strict({
    Count: t.number,
    LastEvaluatedKey,
    Items: t.array(Draft),
    ScannedCount: t.number,
})

export type IRawDraftSearchResult = t.TypeOf<typeof RawDraftSearchResult>
